// Identities Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const identitiesSamples = {
  fundamental: [
    {
      id: 'identities_fund_01',
      problem: "Prove that (a + b)² = a² + 2ab + b²",
      problemType: "identities",
      difficulty: "fundamental",
      description: "Basic square of binomial identity",
      graphData: {
        nodes: [
          {
            id: "binomial_square",
            name: "Binomial Square",
            type: "concepts",
            description: "Square of a binomial expression",
            value: "(a + b)²",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "expansion",
            name: "Expansion",
            type: "concepts",
            description: "Multiply the binomial by itself",
            value: "(a + b)(a + b)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "foil_method",
            name: "FOIL Method",
            type: "concepts",
            description: "First, Outer, Inner, Last multiplication",
            value: "a² + ab + ab + b²",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "concepts",
            description: "Combine like terms",
            value: "a² + 2ab + b²",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a + b)² = a² + 2ab + b²",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "binomial_formula",
            name: "Binomial Square Formula",
            type: "formulas",
            description: "(a + b)² = a² + 2ab + b²",
            value: "(a + b)² = a² + 2ab + b²",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_binomial",
            name: "Expand Binomial",
            type: "steps",
            description: "Write the binomial as a product",
            value: "Step 1: (a + b)² = (a + b)(a + b)",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_foil",
            name: "Apply FOIL Method",
            type: "steps",
            description: "Use FOIL method to multiply",
            value: "Step 2: (a + b)(a + b) = a² + ab + ab + b²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "combine_terms",
            name: "Combine Like Terms",
            type: "steps",
            description: "Add the like terms together",
            value: "Step 3: a² + ab + ab + b² = a² + 2ab + b²",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_identity",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a + b)² = a² + 2ab + b²",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "binomial_square", to: "expansion", type: "expands to", strength: 9 },
          { from: "expansion", to: "foil_method", type: "uses", strength: 9 },
          { from: "foil_method", to: "simplification", type: "leads to", strength: 9 },
          { from: "simplification", to: "result", type: "equals", strength: 10 },
          { from: "binomial_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_binomial", to: "apply_foil", type: "enables", strength: 9 },
          { from: "apply_foil", to: "combine_terms", type: "leads to", strength: 10 },
          { from: "combine_terms", to: "write_identity", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_fund_02',
      problem: "Prove that (a - b)² = a² - 2ab + b²",
      problemType: "identities",
      difficulty: "fundamental",
      description: "Square of difference identity",
      graphData: {
        nodes: [
          {
            id: "difference_square",
            name: "Difference Square",
            type: "concepts",
            description: "Square of a difference expression",
            value: "(a - b)²",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "expansion",
            name: "Expansion",
            type: "concepts",
            description: "Multiply the difference by itself",
            value: "(a - b)(a - b)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "foil_method",
            name: "FOIL Method",
            type: "concepts",
            description: "First, Outer, Inner, Last multiplication",
            value: "a² - ab - ab + b²",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "concepts",
            description: "Combine like terms",
            value: "a² - 2ab + b²",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a - b)² = a² - 2ab + b²",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "difference_square_formula",
            name: "Difference Square Formula",
            type: "formulas",
            description: "(a - b)² = a² - 2ab + b²",
            value: "(a - b)² = a² - 2ab + b²",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_difference",
            name: "Expand Difference",
            type: "steps",
            description: "Write the difference as a product",
            value: "Step 1: (a - b)² = (a - b)(a - b)",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_foil_difference",
            name: "Apply FOIL Method",
            type: "steps",
            description: "Use FOIL method to multiply",
            value: "Step 2: (a - b)(a - b) = a² - ab - ab + b²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "combine_terms_difference",
            name: "Combine Like Terms",
            type: "steps",
            description: "Add the like terms together",
            value: "Step 3: a² - ab - ab + b² = a² - 2ab + b²",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_identity_difference",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a - b)² = a² - 2ab + b²",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "difference_square", to: "expansion", type: "expands to", strength: 9 },
          { from: "expansion", to: "foil_method", type: "uses", strength: 9 },
          { from: "foil_method", to: "simplification", type: "leads to", strength: 9 },
          { from: "simplification", to: "result", type: "equals", strength: 10 },
          { from: "difference_square_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_difference", to: "apply_foil_difference", type: "enables", strength: 9 },
          { from: "apply_foil_difference", to: "combine_terms_difference", type: "leads to", strength: 10 },
          { from: "combine_terms_difference", to: "write_identity_difference", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_fund_03',
      problem: "Prove that (a + b)(a - b) = a² - b²",
      problemType: "identities",
      difficulty: "fundamental",
      description: "Difference of squares identity",
      graphData: {
        nodes: [
          {
            id: "product_difference",
            name: "Product of Sum and Difference",
            type: "concepts",
            description: "Product of sum and difference of same terms",
            value: "(a + b)(a - b)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "foil_method",
            name: "FOIL Method",
            type: "concepts",
            description: "First, Outer, Inner, Last multiplication",
            value: "a² - ab + ab - b²",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "cancellation",
            name: "Cancellation",
            type: "concepts",
            description: "Middle terms cancel out",
            value: "a² - b²",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a + b)(a - b) = a² - b²",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "difference_of_squares_formula",
            name: "Difference of Squares Formula",
            type: "formulas",
            description: "(a + b)(a - b) = a² - b²",
            value: "(a + b)(a - b) = a² - b²",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_product",
            name: "Expand Product",
            type: "steps",
            description: "Use FOIL method to multiply",
            value: "Step 1: (a + b)(a - b) = a² - ab + ab - b²",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "cancel_terms",
            name: "Cancel Middle Terms",
            type: "steps",
            description: "The middle terms cancel out",
            value: "Step 2: a² - ab + ab - b² = a² - b²",
            importance: 10,
            x: 250, y: -50, z: 0
          },
          {
            id: "write_identity_squares",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 3: (a + b)(a - b) = a² - b²",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "product_difference", to: "foil_method", type: "uses", strength: 9 },
          { from: "foil_method", to: "cancellation", type: "leads to", strength: 9 },
          { from: "cancellation", to: "result", type: "equals", strength: 10 },
          { from: "difference_of_squares_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_product", to: "cancel_terms", type: "leads to", strength: 10 },
          { from: "cancel_terms", to: "write_identity_squares", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_fund_04',
      problem: "Prove that (x + 2)(x + 3) = x² + 5x + 6",
      problemType: "identities",
      difficulty: "fundamental",
      description: "Specific binomial multiplication",
      graphData: {
        nodes: [
          {
            id: "binomial_product",
            name: "Binomial Product",
            type: "concepts",
            description: "Product of two binomials",
            value: "(x + 2)(x + 3)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "foil_method",
            name: "FOIL Method",
            type: "concepts",
            description: "First, Outer, Inner, Last multiplication",
            value: "x² + 3x + 2x + 6",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "concepts",
            description: "Combine like terms",
            value: "x² + 5x + 6",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(x + 2)(x + 3) = x² + 5x + 6",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "binomial_multiplication_formula",
            name: "Binomial Multiplication Formula",
            type: "formulas",
            description: "(x + a)(x + b) = x² + (a+b)x + ab",
            value: "(x + a)(x + b) = x² + (a+b)x + ab",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_coefficients",
            name: "Identify Coefficients",
            type: "steps",
            description: "Extract a=2, b=3 from the binomials",
            value: "Step 1: a=2, b=3",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_foil_binomial",
            name: "Apply FOIL Method",
            type: "steps",
            description: "Use FOIL method to multiply",
            value: "Step 2: (x + 2)(x + 3) = x² + 3x + 2x + 6",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "combine_terms_binomial",
            name: "Combine Like Terms",
            type: "steps",
            description: "Add the like terms together",
            value: "Step 3: x² + 3x + 2x + 6 = x² + 5x + 6",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_identity_binomial",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (x + 2)(x + 3) = x² + 5x + 6",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "binomial_product", to: "foil_method", type: "uses", strength: 9 },
          { from: "foil_method", to: "simplification", type: "leads to", strength: 9 },
          { from: "simplification", to: "result", type: "equals", strength: 10 },
          { from: "binomial_multiplication_formula", to: "result", type: "gives", strength: 10 },
          { from: "identify_coefficients", to: "apply_foil_binomial", type: "enables", strength: 8 },
          { from: "apply_foil_binomial", to: "combine_terms_binomial", type: "leads to", strength: 10 },
          { from: "combine_terms_binomial", to: "write_identity_binomial", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_fund_05',
      problem: "Prove that (a + b)³ = a³ + 3a²b + 3ab² + b³",
      problemType: "identities",
      difficulty: "fundamental",
      description: "Cube of binomial identity",
      graphData: {
        nodes: [
          {
            id: "binomial_cube",
            name: "Binomial Cube",
            type: "concepts",
            description: "Cube of a binomial expression",
            value: "(a + b)³",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "expansion",
            name: "Expansion",
            type: "concepts",
            description: "Multiply the binomial three times",
            value: "(a + b)(a + b)(a + b)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step_by_step",
            name: "Step by Step",
            type: "concepts",
            description: "Multiply step by step",
            value: "First: (a + b)² = a² + 2ab + b²",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "final_multiply",
            name: "Final Multiplication",
            type: "concepts",
            description: "Multiply by (a + b) again",
            value: "(a² + 2ab + b²)(a + b)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a + b)³ = a³ + 3a²b + 3ab² + b³",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "binomial_cube_formula",
            name: "Binomial Cube Formula",
            type: "formulas",
            description: "(a + b)³ = a³ + 3a²b + 3ab² + b³",
            value: "(a + b)³ = a³ + 3a²b + 3ab² + b³",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_binomial_cube",
            name: "Expand Binomial Cube",
            type: "steps",
            description: "Write as (a + b)(a + b)(a + b)",
            value: "Step 1: (a + b)³ = (a + b)(a + b)(a + b)",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "first_multiplication",
            name: "First Multiplication",
            type: "steps",
            description: "Multiply first two factors",
            value: "Step 2: (a + b)(a + b) = a² + 2ab + b²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "second_multiplication",
            name: "Second Multiplication",
            type: "steps",
            description: "Multiply result by (a + b)",
            value: "Step 3: (a² + 2ab + b²)(a + b) = a³ + 3a²b + 3ab² + b³",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_identity_cube",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a + b)³ = a³ + 3a²b + 3ab² + b³",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "binomial_cube", to: "expansion", type: "expands to", strength: 9 },
          { from: "expansion", to: "step_by_step", type: "requires", strength: 9 },
          { from: "step_by_step", to: "final_multiply", type: "leads to", strength: 9 },
          { from: "final_multiply", to: "result", type: "equals", strength: 10 },
          { from: "binomial_cube_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_binomial_cube", to: "first_multiplication", type: "enables", strength: 9 },
          { from: "first_multiplication", to: "second_multiplication", type: "enables", strength: 9 },
          { from: "second_multiplication", to: "write_identity_cube", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'identities_inter_01',
      problem: "Prove that (a + b + c)² = a² + b² + c² + 2ab + 2ac + 2bc",
      problemType: "identities",
      difficulty: "intermediate",
      description: "Square of trinomial identity",
      graphData: {
        nodes: [
          {
            id: "trinomial_square",
            name: "Trinomial Square",
            type: "concepts",
            description: "Square of a trinomial expression",
            value: "(a + b + c)²",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "expansion",
            name: "Expansion",
            type: "concepts",
            description: "Multiply the trinomial by itself",
            value: "(a + b + c)(a + b + c)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "systematic_multiplication",
            name: "Systematic Multiplication",
            type: "concepts",
            description: "Multiply each term by every other term",
            value: "a² + ab + ac + ba + b² + bc + ca + cb + c²",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "concepts",
            description: "Combine like terms",
            value: "a² + b² + c² + 2ab + 2ac + 2bc",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a + b + c)² = a² + b² + c² + 2ab + 2ac + 2bc",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "trinomial_square_formula",
            name: "Trinomial Square Formula",
            type: "formulas",
            description: "(a + b + c)² = a² + b² + c² + 2ab + 2ac + 2bc",
            value: "(a + b + c)² = a² + b² + c² + 2ab + 2ac + 2bc",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_trinomial",
            name: "Expand Trinomial",
            type: "steps",
            description: "Write as (a + b + c)(a + b + c)",
            value: "Step 1: (a + b + c)² = (a + b + c)(a + b + c)",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "multiply_each_term",
            name: "Multiply Each Term",
            type: "steps",
            description: "Multiply each term by every other term",
            value: "Step 2: a² + ab + ac + ba + b² + bc + ca + cb + c²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "combine_like_terms",
            name: "Combine Like Terms",
            type: "steps",
            description: "Combine like terms and simplify",
            value: "Step 3: a² + b² + c² + 2ab + 2ac + 2bc",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_identity_trinomial",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a + b + c)² = a² + b² + c² + 2ab + 2ac + 2bc",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "trinomial_square", to: "expansion", type: "expands to", strength: 9 },
          { from: "expansion", to: "systematic_multiplication", type: "uses", strength: 9 },
          { from: "systematic_multiplication", to: "simplification", type: "leads to", strength: 9 },
          { from: "simplification", to: "result", type: "equals", strength: 10 },
          { from: "trinomial_square_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_trinomial", to: "multiply_each_term", type: "enables", strength: 9 },
          { from: "multiply_each_term", to: "combine_like_terms", type: "leads to", strength: 10 },
          { from: "combine_like_terms", to: "write_identity_trinomial", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_inter_02',
      problem: "Prove that (a + b)(a² - ab + b²) = a³ + b³",
      problemType: "identities",
      difficulty: "intermediate",
      description: "Sum of cubes identity",
      graphData: {
        nodes: [
          {
            id: "sum_cubes",
            name: "Sum of Cubes",
            type: "concepts",
            description: "Product that gives sum of cubes",
            value: "(a + b)(a² - ab + b²)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "distribution",
            name: "Distribution",
            type: "concepts",
            description: "Distribute (a + b) to each term",
            value: "a³ - a²b + ab² + a²b - ab² + b³",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "cancellation",
            name: "Cancellation",
            type: "concepts",
            description: "Middle terms cancel out",
            value: "a³ + b³",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a + b)(a² - ab + b²) = a³ + b³",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "sum_cubes_formula",
            name: "Sum of Cubes Formula",
            type: "formulas",
            description: "(a + b)(a² - ab + b²) = a³ + b³",
            value: "(a + b)(a² - ab + b²) = a³ + b³",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_sum_cubes",
            name: "Expand Sum of Cubes",
            type: "steps",
            description: "Write the product expression",
            value: "Step 1: (a + b)(a² - ab + b²)",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "distribute_terms",
            name: "Distribute Terms",
            type: "steps",
            description: "Distribute (a + b) to each term",
            value: "Step 2: a³ - a²b + ab² + a²b - ab² + b³",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "cancel_middle_terms",
            name: "Cancel Middle Terms",
            type: "steps",
            description: "Cancel out the middle terms",
            value: "Step 3: a³ - a²b + ab² + a²b - ab² + b³ = a³ + b³",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_sum_cubes_identity",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a + b)(a² - ab + b²) = a³ + b³",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "sum_cubes", to: "distribution", type: "uses", strength: 9 },
          { from: "distribution", to: "cancellation", type: "leads to", strength: 9 },
          { from: "cancellation", to: "result", type: "equals", strength: 10 },
          { from: "sum_cubes_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_sum_cubes", to: "distribute_terms", type: "enables", strength: 9 },
          { from: "distribute_terms", to: "cancel_middle_terms", type: "leads to", strength: 10 },
          { from: "cancel_middle_terms", to: "write_sum_cubes_identity", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_inter_03',
      problem: "Prove that (a - b)(a² + ab + b²) = a³ - b³",
      problemType: "identities",
      difficulty: "intermediate",
      description: "Difference of cubes identity",
      graphData: {
        nodes: [
          {
            id: "difference_cubes",
            name: "Difference of Cubes",
            type: "concepts",
            description: "Product that gives difference of cubes",
            value: "(a - b)(a² + ab + b²)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "distribution",
            name: "Distribution",
            type: "concepts",
            description: "Distribute (a - b) to each term",
            value: "a³ + a²b + ab² - a²b - ab² - b³",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "cancellation",
            name: "Cancellation",
            type: "concepts",
            description: "Middle terms cancel out",
            value: "a³ - b³",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a - b)(a² + ab + b²) = a³ - b³",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "difference_cubes_formula",
            name: "Difference of Cubes Formula",
            type: "formulas",
            description: "(a - b)(a² + ab + b²) = a³ - b³",
            value: "(a - b)(a² + ab + b²) = a³ - b³",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_difference_cubes",
            name: "Expand Difference of Cubes",
            type: "steps",
            description: "Write the product expression",
            value: "Step 1: (a - b)(a² + ab + b²)",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "distribute_difference_terms",
            name: "Distribute Terms",
            type: "steps",
            description: "Distribute (a - b) to each term",
            value: "Step 2: a³ + a²b + ab² - a²b - ab² - b³",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "cancel_difference_terms",
            name: "Cancel Middle Terms",
            type: "steps",
            description: "Cancel out the middle terms",
            value: "Step 3: a³ + a²b + ab² - a²b - ab² - b³ = a³ - b³",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_difference_cubes_identity",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a - b)(a² + ab + b²) = a³ - b³",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "difference_cubes", to: "distribution", type: "uses", strength: 9 },
          { from: "distribution", to: "cancellation", type: "leads to", strength: 9 },
          { from: "cancellation", to: "result", type: "equals", strength: 10 },
          { from: "difference_cubes_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_difference_cubes", to: "distribute_difference_terms", type: "enables", strength: 9 },
          { from: "distribute_difference_terms", to: "cancel_difference_terms", type: "leads to", strength: 10 },
          { from: "cancel_difference_terms", to: "write_difference_cubes_identity", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_inter_04',
      problem: "Prove that (x + y + z)(x² + y² + z² - xy - yz - zx) = x³ + y³ + z³ - 3xyz",
      problemType: "identities",
      difficulty: "intermediate",
      description: "Complex trinomial identity",
      graphData: {
        nodes: [
          {
            id: "complex_product",
            name: "Complex Product",
            type: "concepts",
            description: "Product of trinomial and quadratic expression",
            value: "(x + y + z)(x² + y² + z² - xy - yz - zx)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "systematic_distribution",
            name: "Systematic Distribution",
            type: "concepts",
            description: "Distribute each term systematically",
            value: "x³ + xy² + xz² - x²y - xyz - x²z + ...",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "concepts",
            description: "Combine and cancel terms",
            value: "x³ + y³ + z³ - 3xyz",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(x + y + z)(x² + y² + z² - xy - yz - zx) = x³ + y³ + z³ - 3xyz",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "complex_trinomial_formula",
            name: "Complex Trinomial Formula",
            type: "formulas",
            description: "(x + y + z)(x² + y² + z² - xy - yz - zx) = x³ + y³ + z³ - 3xyz",
            value: "(x + y + z)(x² + y² + z² - xy - yz - zx) = x³ + y³ + z³ - 3xyz",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_complex_trinomial",
            name: "Expand Complex Trinomial",
            type: "steps",
            description: "Write the product expression",
            value: "Step 1: (x + y + z)(x² + y² + z² - xy - yz - zx)",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "distribute_complex_terms",
            name: "Distribute Complex Terms",
            type: "steps",
            description: "Distribute each term systematically",
            value: "Step 2: x³ + xy² + xz² - x²y - xyz - x²z + yx² + y³ + yz² - xy² - y²z - xyz + zx² + zy² + z³ - xyz - yz² - xz²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "simplify_complex_terms",
            name: "Simplify Complex Terms",
            type: "steps",
            description: "Combine and cancel terms",
            value: "Step 3: x³ + y³ + z³ - 3xyz",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_complex_identity",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (x + y + z)(x² + y² + z² - xy - yz - zx) = x³ + y³ + z³ - 3xyz",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "complex_product", to: "systematic_distribution", type: "uses", strength: 9 },
          { from: "systematic_distribution", to: "simplification", type: "leads to", strength: 9 },
          { from: "simplification", to: "result", type: "equals", strength: 10 },
          { from: "complex_trinomial_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_complex_trinomial", to: "distribute_complex_terms", type: "enables", strength: 9 },
          { from: "distribute_complex_terms", to: "simplify_complex_terms", type: "leads to", strength: 10 },
          { from: "simplify_complex_terms", to: "write_complex_identity", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_inter_05',
      problem: "Prove that (a + b)⁴ = a⁴ + 4a³b + 6a²b² + 4ab³ + b⁴",
      problemType: "identities",
      difficulty: "intermediate",
      description: "Fourth power of binomial",
      graphData: {
        nodes: [
          {
            id: "binomial_fourth",
            name: "Binomial Fourth Power",
            type: "concepts",
            description: "Fourth power of a binomial",
            value: "(a + b)⁴",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "pascal_triangle",
            name: "Pascal's Triangle",
            type: "concepts",
            description: "Use coefficients from Pascal's triangle",
            value: "1, 4, 6, 4, 1",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "expansion",
            name: "Expansion",
            type: "concepts",
            description: "Apply binomial theorem",
            value: "a⁴ + 4a³b + 6a²b² + 4ab³ + b⁴",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a + b)⁴ = a⁴ + 4a³b + 6a²b² + 4ab³ + b⁴",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "binomial_fourth_formula",
            name: "Binomial Fourth Power Formula",
            type: "formulas",
            description: "(a + b)⁴ = a⁴ + 4a³b + 6a²b² + 4ab³ + b⁴",
            value: "(a + b)⁴ = a⁴ + 4a³b + 6a²b² + 4ab³ + b⁴",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_binomial_fourth",
            name: "Expand Binomial Fourth",
            type: "steps",
            description: "Write the fourth power expression",
            value: "Step 1: (a + b)⁴",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "use_pascal_coefficients",
            name: "Use Pascal's Triangle",
            type: "steps",
            description: "Apply coefficients from Pascal's triangle",
            value: "Step 2: Coefficients 1, 4, 6, 4, 1",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_binomial_theorem",
            name: "Apply Binomial Theorem",
            type: "steps",
            description: "Apply the binomial theorem formula",
            value: "Step 3: a⁴ + 4a³b + 6a²b² + 4ab³ + b⁴",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_fourth_identity",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a + b)⁴ = a⁴ + 4a³b + 6a²b² + 4ab³ + b⁴",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "binomial_fourth", to: "pascal_triangle", type: "uses", strength: 9 },
          { from: "pascal_triangle", to: "expansion", type: "leads to", strength: 9 },
          { from: "expansion", to: "result", type: "equals", strength: 10 },
          { from: "binomial_fourth_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_binomial_fourth", to: "use_pascal_coefficients", type: "enables", strength: 9 },
          { from: "use_pascal_coefficients", to: "apply_binomial_theorem", type: "leads to", strength: 10 },
          { from: "apply_binomial_theorem", to: "write_fourth_identity", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'identities_hard_01',
      problem: "Prove that (a + b + c)³ = a³ + b³ + c³ + 3(a + b + c)(ab + bc + ca) - 3abc",
      problemType: "identities",
      difficulty: "harder",
      description: "Cube of trinomial identity",
      graphData: {
        nodes: [
          {
            id: "trinomial_cube",
            name: "Trinomial Cube",
            type: "concepts",
            description: "Cube of a trinomial expression",
            value: "(a + b + c)³",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "complex_expansion",
            name: "Complex Expansion",
            type: "concepts",
            description: "Expand using multiple steps",
            value: "(a + b + c)(a + b + c)(a + b + c)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "systematic_approach",
            name: "Systematic Approach",
            type: "concepts",
            description: "Use systematic multiplication",
            value: "a³ + b³ + c³ + 3a²b + 3a²c + 3ab² + 3ac² + 3b²c + 3bc² + 6abc",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "rearrangement",
            name: "Rearrangement",
            type: "concepts",
            description: "Rearrange terms to match identity",
            value: "a³ + b³ + c³ + 3(a + b + c)(ab + bc + ca) - 3abc",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a + b + c)³ = a³ + b³ + c³ + 3(a + b + c)(ab + bc + ca) - 3abc",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "trinomial_cube_formula",
            name: "Trinomial Cube Formula",
            type: "formulas",
            description: "(a + b + c)³ = a³ + b³ + c³ + 3(a + b + c)(ab + bc + ca) - 3abc",
            value: "(a + b + c)³ = a³ + b³ + c³ + 3(a + b + c)(ab + bc + ca) - 3abc",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_trinomial_cube",
            name: "Expand Trinomial Cube",
            type: "steps",
            description: "Write the cube expression",
            value: "Step 1: (a + b + c)³",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "systematic_multiplication",
            name: "Systematic Multiplication",
            type: "steps",
            description: "Multiply systematically",
            value: "Step 2: a³ + b³ + c³ + 3a²b + 3a²c + 3ab² + 3ac² + 3b²c + 3bc² + 6abc",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "rearrange_terms",
            name: "Rearrange Terms",
            type: "steps",
            description: "Rearrange to match identity",
            value: "Step 3: a³ + b³ + c³ + 3(a + b + c)(ab + bc + ca) - 3abc",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_trinomial_cube_identity",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a + b + c)³ = a³ + b³ + c³ + 3(a + b + c)(ab + bc + ca) - 3abc",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "trinomial_cube", to: "complex_expansion", type: "expands to", strength: 9 },
          { from: "complex_expansion", to: "systematic_approach", type: "uses", strength: 9 },
          { from: "systematic_approach", to: "rearrangement", type: "leads to", strength: 9 },
          { from: "rearrangement", to: "result", type: "equals", strength: 10 },
          { from: "trinomial_cube_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_trinomial_cube", to: "systematic_multiplication", type: "enables", strength: 9 },
          { from: "systematic_multiplication", to: "rearrange_terms", type: "leads to", strength: 10 },
          { from: "rearrange_terms", to: "write_trinomial_cube_identity", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_hard_02',
      problem: "Prove that (a + b + c + d)² = a² + b² + c² + d² + 2(ab + ac + ad + bc + bd + cd)",
      problemType: "identities",
      difficulty: "harder",
      description: "Square of quadrinomial identity",
      graphData: {
        nodes: [
          {
            id: "quadrinomial_square",
            name: "Quadrinomial Square",
            type: "concepts",
            description: "Square of a quadrinomial expression",
            value: "(a + b + c + d)²",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "systematic_expansion",
            name: "Systematic Expansion",
            type: "concepts",
            description: "Multiply each term by every other term",
            value: "a² + ab + ac + ad + ba + b² + bc + bd + ca + cb + c² + cd + da + db + dc + d²",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "grouping",
            name: "Grouping",
            type: "concepts",
            description: "Group like terms together",
            value: "a² + b² + c² + d² + 2ab + 2ac + 2ad + 2bc + 2bd + 2cd",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a + b + c + d)² = a² + b² + c² + d² + 2(ab + ac + ad + bc + bd + cd)",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "quadrinomial_square_formula",
            name: "Quadrinomial Square Formula",
            type: "formulas",
            description: "(a + b + c + d)² = a² + b² + c² + d² + 2(ab + ac + ad + bc + bd + cd)",
            value: "(a + b + c + d)² = a² + b² + c² + d² + 2(ab + ac + ad + bc + bd + cd)",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_quadrinomial",
            name: "Expand Quadrinomial",
            type: "steps",
            description: "Write the square expression",
            value: "Step 1: (a + b + c + d)²",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "multiply_each_term",
            name: "Multiply Each Term",
            type: "steps",
            description: "Multiply each term by every other term",
            value: "Step 2: a² + ab + ac + ad + ba + b² + bc + bd + ca + cb + c² + cd + da + db + dc + d²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "group_like_terms",
            name: "Group Like Terms",
            type: "steps",
            description: "Group like terms together",
            value: "Step 3: a² + b² + c² + d² + 2ab + 2ac + 2ad + 2bc + 2bd + 2cd",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_quadrinomial_identity",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a + b + c + d)² = a² + b² + c² + d² + 2(ab + ac + ad + bc + bd + cd)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "quadrinomial_square", to: "systematic_expansion", type: "uses", strength: 9 },
          { from: "systematic_expansion", to: "grouping", type: "leads to", strength: 9 },
          { from: "grouping", to: "result", type: "equals", strength: 10 },
          { from: "quadrinomial_square_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_quadrinomial", to: "multiply_each_term", type: "enables", strength: 9 },
          { from: "multiply_each_term", to: "group_like_terms", type: "leads to", strength: 10 },
          { from: "group_like_terms", to: "write_quadrinomial_identity", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_hard_03',
      problem: "Prove that (a + b + c)(a² + b² + c² - ab - bc - ca) = a³ + b³ + c³ - 3abc",
      problemType: "identities",
      difficulty: "harder",
      description: "Complex trinomial product identity",
      graphData: {
        nodes: [
          {
            id: "complex_trinomial",
            name: "Complex Trinomial Product",
            type: "concepts",
            description: "Product of trinomial and quadratic expression",
            value: "(a + b + c)(a² + b² + c² - ab - bc - ca)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "distribution",
            name: "Distribution",
            type: "concepts",
            description: "Distribute each term of trinomial",
            value: "a³ + ab² + ac² - a²b - abc - a²c + a²b + b³ + bc² - ab² - b²c - abc + a²c + b²c + c³ - abc - bc² - ac²",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "cancellation",
            name: "Cancellation",
            type: "concepts",
            description: "Cancel out like terms",
            value: "a³ + b³ + c³ - 3abc",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a + b + c)(a² + b² + c² - ab - bc - ca) = a³ + b³ + c³ - 3abc",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "complex_trinomial_product_formula",
            name: "Complex Trinomial Product Formula",
            type: "formulas",
            description: "(a + b + c)(a² + b² + c² - ab - bc - ca) = a³ + b³ + c³ - 3abc",
            value: "(a + b + c)(a² + b² + c² - ab - bc - ca) = a³ + b³ + c³ - 3abc",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_complex_trinomial_product",
            name: "Expand Complex Trinomial Product",
            type: "steps",
            description: "Write the product expression",
            value: "Step 1: (a + b + c)(a² + b² + c² - ab - bc - ca)",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "distribute_complex_terms",
            name: "Distribute Complex Terms",
            type: "steps",
            description: "Distribute each term of trinomial",
            value: "Step 2: a³ + ab² + ac² - a²b - abc - a²c + a²b + b³ + bc² - ab² - b²c - abc + a²c + b²c + c³ - abc - bc² - ac²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "cancel_complex_terms",
            name: "Cancel Complex Terms",
            type: "steps",
            description: "Cancel out like terms",
            value: "Step 3: a³ + b³ + c³ - 3abc",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_complex_trinomial_identity",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a + b + c)(a² + b² + c² - ab - bc - ca) = a³ + b³ + c³ - 3abc",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "complex_trinomial", to: "distribution", type: "uses", strength: 9 },
          { from: "distribution", to: "cancellation", type: "leads to", strength: 9 },
          { from: "cancellation", to: "result", type: "equals", strength: 10 },
          { from: "complex_trinomial_product_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_complex_trinomial_product", to: "distribute_complex_terms", type: "enables", strength: 9 },
          { from: "distribute_complex_terms", to: "cancel_complex_terms", type: "leads to", strength: 10 },
          { from: "cancel_complex_terms", to: "write_complex_trinomial_identity", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_hard_04',
      problem: "Prove that (a + b + c)⁴ = a⁴ + b⁴ + c⁴ + 4(a³b + a³c + b³a + b³c + c³a + c³b) + 6(a²b² + a²c² + b²c²) + 12abc(a + b + c)",
      problemType: "identities",
      difficulty: "harder",
      description: "Fourth power of trinomial",
      graphData: {
        nodes: [
          {
            id: "trinomial_fourth",
            name: "Trinomial Fourth Power",
            type: "concepts",
            description: "Fourth power of a trinomial",
            value: "(a + b + c)⁴",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "multistep_expansion",
            name: "Multi-step Expansion",
            type: "concepts",
            description: "Expand using multiple steps",
            value: "First find (a + b + c)², then square the result",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "systematic_collection",
            name: "Systematic Collection",
            type: "concepts",
            description: "Collect all terms systematically",
            value: "a⁴ + b⁴ + c⁴ + 4(a³b + a³c + b³a + b³c + c³a + c³b) + 6(a²b² + a²c² + b²c²) + 12abc(a + b + c)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a + b + c)⁴ = a⁴ + b⁴ + c⁴ + 4(a³b + a³c + b³a + b³c + c³a + c³b) + 6(a²b² + a²c² + b²c²) + 12abc(a + b + c)",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "trinomial_fourth_formula",
            name: "Trinomial Fourth Power Formula",
            type: "formulas",
            description: "(a + b + c)⁴ = a⁴ + b⁴ + c⁴ + 4(a³b + a³c + b³a + b³c + c³a + c³b) + 6(a²b² + a²c² + b²c²) + 12abc(a + b + c)",
            value: "(a + b + c)⁴ = a⁴ + b⁴ + c⁴ + 4(a³b + a³c + b³a + b³c + c³a + c³b) + 6(a²b² + a²c² + b²c²) + 12abc(a + b + c)",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_trinomial_fourth",
            name: "Expand Trinomial Fourth",
            type: "steps",
            description: "Write the fourth power expression",
            value: "Step 1: (a + b + c)⁴",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "use_multistep_expansion",
            name: "Use Multi-step Expansion",
            type: "steps",
            description: "First find (a + b + c)², then square the result",
            value: "Step 2: First find (a + b + c)², then square the result",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "collect_systematic_terms",
            name: "Collect Systematic Terms",
            type: "steps",
            description: "Collect all terms systematically",
            value: "Step 3: a⁴ + b⁴ + c⁴ + 4(a³b + a³c + b³a + b³c + c³a + c³b) + 6(a²b² + a²c² + b²c²) + 12abc(a + b + c)",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_trinomial_fourth_identity",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a + b + c)⁴ = a⁴ + b⁴ + c⁴ + 4(a³b + a³c + b³a + b³c + c³a + c³b) + 6(a²b² + a²c² + b²c²) + 12abc(a + b + c)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "trinomial_fourth", to: "multistep_expansion", type: "uses", strength: 9 },
          { from: "multistep_expansion", to: "systematic_collection", type: "leads to", strength: 9 },
          { from: "systematic_collection", to: "result", type: "equals", strength: 10 },
          { from: "trinomial_fourth_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_trinomial_fourth", to: "use_multistep_expansion", type: "enables", strength: 9 },
          { from: "use_multistep_expansion", to: "collect_systematic_terms", type: "leads to", strength: 10 },
          { from: "collect_systematic_terms", to: "write_trinomial_fourth_identity", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'identities_hard_05',
      problem: "Prove that (a + b + c + d)(a³ + b³ + c³ + d³ - a²b - a²c - a²d - b²a - b²c - b²d - c²a - c²b - c²d - d²a - d²b - d²c) = a⁴ + b⁴ + c⁴ + d⁴ - 4abcd",
      problemType: "identities",
      difficulty: "harder",
      description: "Complex quadrinomial identity",
      graphData: {
        nodes: [
          {
            id: "complex_quadrinomial",
            name: "Complex Quadrinomial Product",
            type: "concepts",
            description: "Product of quadrinomial and cubic expression",
            value: "(a + b + c + d)(a³ + b³ + c³ + d³ - a²b - a²c - a²d - b²a - b²c - b²d - c²a - c²b - c²d - d²a - d²b - d²c)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "extensive_distribution",
            name: "Extensive Distribution",
            type: "concepts",
            description: "Distribute each term systematically",
            value: "a⁴ + ab³ + ac³ + ad³ - a³b - a³c - a³d + ...",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "massive_cancellation",
            name: "Massive Cancellation",
            type: "concepts",
            description: "Cancel out all cross terms",
            value: "a⁴ + b⁴ + c⁴ + d⁴ - 4abcd",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Identity Result",
            type: "values",
            description: "The proven identity",
            value: "(a + b + c + d)(a³ + b³ + c³ + d³ - a²b - a²c - a²d - b²a - b²c - b²d - c²a - c²b - c²d - d²a - d²b - d²c) = a⁴ + b⁴ + c⁴ + d⁴ - 4abcd",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "complex_quadrinomial_formula",
            name: "Complex Quadrinomial Formula",
            type: "formulas",
            description: "(a + b + c + d)(a³ + b³ + c³ + d³ - a²b - a²c - a²d - b²a - b²c - b²d - c²a - c²b - c²d - d²a - d²b - d²c) = a⁴ + b⁴ + c⁴ + d⁴ - 4abcd",
            value: "(a + b + c + d)(a³ + b³ + c³ + d³ - a²b - a²c - a²d - b²a - b²c - b²d - c²a - c²b - c²d - d²a - d²b - d²c) = a⁴ + b⁴ + c⁴ + d⁴ - 4abcd",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "expand_complex_quadrinomial",
            name: "Expand Complex Quadrinomial",
            type: "steps",
            description: "Write the product expression",
            value: "Step 1: (a + b + c + d)(a³ + b³ + c³ + d³ - a²b - a²c - a²d - b²a - b²c - b²d - c²a - c²b - c²d - d²a - d²b - d²c)",
            importance: 9,
            x: -250, y: -50, z: 0
          },
          {
            id: "distribute_extensive_terms",
            name: "Distribute Extensive Terms",
            type: "steps",
            description: "Distribute each term systematically",
            value: "Step 2: a⁴ + ab³ + ac³ + ad³ - a³b - a³c - a³d + ...",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "cancel_massive_terms",
            name: "Cancel Massive Terms",
            type: "steps",
            description: "Cancel out all cross terms",
            value: "Step 3: a⁴ + b⁴ + c⁴ + d⁴ - 4abcd",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_complex_quadrinomial_identity",
            name: "Write Identity",
            type: "steps",
            description: "Write the final identity",
            value: "Step 4: (a + b + c + d)(a³ + b³ + c³ + d³ - a²b - a²c - a²d - b²a - b²c - b²d - c²a - c²b - c²d - d²a - d²b - d²c) = a⁴ + b⁴ + c⁴ + d⁴ - 4abcd",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "complex_quadrinomial", to: "extensive_distribution", type: "uses", strength: 9 },
          { from: "extensive_distribution", to: "massive_cancellation", type: "leads to", strength: 9 },
          { from: "massive_cancellation", to: "result", type: "equals", strength: 10 },
          { from: "complex_quadrinomial_formula", to: "result", type: "equals", strength: 10 },
          { from: "expand_complex_quadrinomial", to: "distribute_extensive_terms", type: "enables", strength: 9 },
          { from: "distribute_extensive_terms", to: "cancel_massive_terms", type: "leads to", strength: 10 },
          { from: "cancel_massive_terms", to: "write_complex_quadrinomial_identity", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 